import VueCtkDateTimePicker from 'vue-ctk-date-time-picker';
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css';
import Vue from 'vue';
import pixelWidth from 'string-pixel-width';
import { DateTime } from 'luxon';

class FromArrayBuilder {
  constructor(axios) {
    this.nextBuilder = null;
    this.axios = axios;
  }

  setNext(nextBuilder) {
    this.nextBuilder = nextBuilder;
  }

  async build(formElement) {
    if (Array.isArray(formElement.dataSource)) {
      return formElement.dataSource;
    }

    return await this.nextBuilder.build(formElement);
  }

}

class FnBuilder extends FromArrayBuilder {
  async build(formElement) {
    if (typeof formElement.dataSource == 'function') {
      return formElement.dataSource();
    }

    return await this.nextBuilder.build(formElement);
  }

}

class ApiBuilder extends FromArrayBuilder {
  async build(formElement) {
    if (!formElement.dataSource.endpoint) {
      throw new Error(formElement.component + ' must have defined dataSource property or appropriate api settings.');
    }

    let items = await this.loadDataSourceFromApi(formElement.dataSource, formElement.dataSource.dataProperty, formElement.dataSource.dataFn);
    return items;
  }

  async loadDataSourceFromApi(api, dataProperty, dataExtractionFunction) {
    let items = [];

    try {
      let isMultiple = api.endpoint.split(',').length > 1;

      if (isMultiple) {
        let qs = api.endpoint.split('?')[1];

        if (qs) {
          let qsParams = qs.split('&');
          let lastQsParam = '';

          if (qsParams.length) {
            lastQsParam = qsParams[qsParams.length - 1];
          } else {
            lastQsParam = qsParams;
          }

          let paramName = lastQsParam.split('=')[0];
          let paramValues = lastQsParam.split('=')[1].split(',');
          let qsAppend = '';

          for (let value of paramValues) {
            qsAppend += `${paramName}=${value}&`;
          }

          api.endpoint = api.endpoint.replace(lastQsParam, qsAppend);
        }
      }

      let response = await this.axios({
        url: api.endpoint,
        method: api.method ? api.method : 'GET'
      });
      var data = response.data;

      if (!data) {
        throw new Error('DataSource error: server responded successfully, but returned no data. Endpoint - ' + api.endpoint);
      }

      if (dataExtractionFunction && typeof dataExtractionFunction == 'function') {
        items = dataExtractionFunction(data);
        return;
      }

      if (!dataProperty) {
        items = data;
      } else {
        const properties = dataProperty.split('.');
        let tempData = data;
        properties.forEach(p => {
          tempData = tempData[p];
        });
        items = tempData;
      }

      if (!api.textProperty) {
        api.textProperty = 'name';
      }

      if (!api.valueProperty) {
        api.valueProperty = 'id';
      }

      if (!items.length) {
        return [];
      }

      if (!Object.keys(items[0]).includes(api.textProperty)) {
        throw new Error('API objects do not contain provided textProperty - ' + api.textProperty);
      }

      if (!Object.keys(items[0]).includes(api.valueProperty)) {
        throw new Error('API objects do not contain provided valueProperty - ' + api.valueProperty);
      }

      return items.map(x => {
        return {
          text: x[api.textProperty],
          value: x[api.valueProperty],
          entireObject: x
        };
      });
    } catch (error) {
      throw new Error('Failed to load the datasource from ' + api.endpoint + '.');
    }
  }

}

var Builders = {
  FromArrayBuilder,
  FnBuilder,
  ApiBuilder
};

var vueInstance = null;

function addFirstOption(formElement, dataSource) {
  if (!isMultiple(formElement) && !isRequired(formElement)) {
    dataSource.unshift({
      text: formElement.firstOptionTxt ? translate(formElement.firstOptionTxt) : 'Choose...',
      value: ''
    });
  }

  return dataSource;
}

function translateDataSource(formElement, dataSource) {
  dataSource.forEach(item => {
    item.text = translate(item.text, true);
  });
  return dataSource;
}

function isMultiple(formElement) {
  var _formElement$props;

  return (_formElement$props = formElement.props) === null || _formElement$props === void 0 ? void 0 : _formElement$props.multiple;
}

function isRequired(formElement) {
  var _formElement$rules;

  return (_formElement$rules = formElement.rules) === null || _formElement$rules === void 0 ? void 0 : _formElement$rules.includes('required');
}

function shouldBeTranslated(keyword) {
  return keyword && keyword.indexOf('$') == 0;
}

function translate(translatable, upperFirst) {
  if (!shouldBeTranslated(translatable)) {
    return upperFirst ? capitalize(translatable) : translatable;
  }

  const keyToTranslate = translatable.substring(1);

  if (!vueInstance.$t) {
    console.warn(`Cant translate ${keyToTranslate}, no i18n translator found.`);
    return keyToTranslate;
  }

  return upperFirst ? capitalize(vueInstance.$t(keyToTranslate)) : vueInstance.$t(keyToTranslate);
}

function capitalize(s) {
  if (typeof s !== 'string') return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

let modifiers = [addFirstOption, translateDataSource];
var Modifiers = {
  setVue(vue) {
    vueInstance = vue;
  },

  modify: function (formElement, dataSource) {
    for (let modifier of modifiers) {
      dataSource = modifier(formElement, dataSource);
    }

    return dataSource;
  }
};

var axios = undefined;

async function buildDataSource(formElement) {
  var fnBuilder = new Builders.FnBuilder(axios);
  var apiBuilder = new Builders.ApiBuilder(axios);
  var arrayBuilder = new Builders.FromArrayBuilder(axios);
  fnBuilder.setNext(apiBuilder);
  arrayBuilder.setNext(fnBuilder);
  var dataSource = await arrayBuilder.build(formElement);
  return Modifiers.modify(formElement, dataSource);
}

function shouldContainDataSource(formElement) {
  if (!formElement.component) {
    return false;
  }

  let elementsWithDataSource = ['v-autocomplete', 'v-select'];
  return elementsWithDataSource.includes(formElement.component) || formElement.component.toLowerCase().includes('select');
}

var dataSourceBuilder = {
  buildDataSource,
  shouldContainDataSource,
  setAxios: function (axiosInstance) {
    axios = axiosInstance;
  },
  setVue: function (vueInstance) {
    Modifiers.setVue(vueInstance);
  }
};

async function handleInputFile(file, key, formBuilder, conversionStrategy) {
  let data = undefined;

  if (conversionStrategy == 'base64') {
    if (Array.isArray(file)) {
      data = await getFiles(file);
      data = data.map(x => x.base64StringFile);
    } else {
      data = await getFile(file);
      data = data.base64StringFile;
    }
  } else {
    data = {
      isFormData: true
    };

    if (Array.isArray(file)) {
      data.items = file;
    } else {
      data.items = [file];
    }
  }

  formBuilder.$set(formBuilder.formObject, key + '_upload', data);
}

function getFiles(files) {
  return Promise.all(files.map(file => getFile(file)));
} //take a single JavaScript File object


function getFile(file) {
  var reader = new FileReader();
  return new Promise((resolve, reject) => {
    reader.onerror = () => {
      reader.abort();
      reject(new Error('Error parsing file'));
    };

    reader.onload = function () {
      //This will result in an array that will be recognized by C#.NET WebApi as a byte[]
      let bytes = Array.from(new Uint8Array(this.result)); //if you want the base64encoded file you would use the below line:

      let base64StringFile = btoa(bytes.map(item => String.fromCharCode(item)).join('')); //Resolve the promise with your custom file structure

      resolve({
        bytes: bytes,
        base64StringFile: base64StringFile,
        fileName: file.name,
        fileType: file.type
      });
    };

    reader.readAsArrayBuffer(file);
  });
}

var fileInputHandler = {
  handle: handleInputFile
};

var formBuilderMixin = {
  data() {
    return {
      changeFunctions: {
        dataSource: this.handleDataSourceChange,
        hide: this.handleHide,
        disable: this.handleDisable,
        enable: this.handleEnable,
        clear: this.handleClear,
        order: this.handleOrder,
        cols: this.handleCols,
        value: this.handleValue
      }
    };
  },

  beforeMount() {
    dataSourceBuilder.setAxios(this.$formBuilderAxios);
    dataSourceBuilder.setVue(this);
  },

  methods: {
    toSentenceCase(text) {
      var result = text.replace(/([A-Z])/g, ' $1');
      return result.charAt(0).toUpperCase() + result.slice(1);
    },

    async prepareFormObject() {
      this.formObject = {};

      for (let el of this.formElements) {
        if (dataSourceBuilder.shouldContainDataSource(el)) {
          this.$set(this.dataSources, el.key, await this.dataSource(el));
        }

        this.$set(this.disabled, el.key, el.disabled);
        this.$set(this.hidden, el.key, el.hidden);
      }

      await this.populateValuesBasedOnIncommingObject();
      this.formIsValid = await this.$refs.observer.validate();
    },

    populateValuesBasedOnIncommingObject() {
      if (!this.incommingObject) {
        return;
      }

      for (let el of this.formElements) {
        if (Object.keys(this.incommingObject).includes(el.key)) {
          let incommingValue = this.incommingValue(el.key);

          if (el.props && el.props.multiple && !Array.isArray(incommingValue)) {
            this.$set(this.formObject, el.key, [incommingValue]);
          } else {
            this.$set(this.formObject, el.key, incommingValue);
          }

          this.handleChange(el.key, incommingValue);
        }
      }
    },

    incommingValue(key) {
      return this.incommingObject[key];
    },

    dataSource: async function (formElement) {
      return await dataSourceBuilder.buildDataSource(formElement);
    },

    handleChange(key, value, isDate) {
      this.$refs.observer.validate().then(() => {
        this.$refs.observer.validate().then(x => {
          this.formIsValid = x;
        });

        if (isDate) {
          if (!value) {
            this.$set(this.formObject, key, '');
          }

          return;
        }

        let changeFunctions = this.changeFunctionChain();
        changeFunctions.forEach(x => {
          x(key, value);
        });
        this.eventBus.$emit('formObjectChanged', this.formObject);
      });
    },

    changeFunctionChain() {
      let that = this;

      function affectsChange(key, value) {
        let element = that.find(key);

        if (element && element.affects) {
          for (let toBeAffected of element.affects) {
            that.changeFunctions[toBeAffected.change.type](toBeAffected, value);
          }
        }
      }

      function computationChange(key) {
        if (that.affectsComputation(key)) {
          let affected = that.getAffectedComputations(key);

          for (let affectedItem of affected) {
            let filtered = affectedItem.computation.filter(x => x.when[key]);

            for (let x of filtered) {
              let whenMatched = true;

              for (let whenKey in x.when) {
                if (that.formObject[whenKey] != x.when[whenKey]) {
                  whenMatched = false;
                  break;
                }
              }

              if (whenMatched) {
                that.formObject[affectedItem.key] = x.then.value;
              }

              that.disabled[affectedItem.key] = whenMatched ? x.then.disabled : affectedItem.disabled;
            }
          }
        }
      }

      function fileChoosenChange(key, value) {
        let element = that.find(key);

        if (element.component == 'v-file-input') {
          fileInputHandler.handle(value, key, that, element.conversionStrategy ? element.conversionStrategy : 'base64');
        }
      }

      return [affectsChange, computationChange, fileChoosenChange];
    },

    affectsComputation(key) {
      let affected = this.getAffectedComputations(key);
      return affected.length;
    },

    getAffectedComputations(key) {
      let affectedComputations = this.formElements.filter(x => x.computation != undefined && x.computation.some(x => x.when[key] != undefined));
      return affectedComputations;
    },

    handleHide(toBeAffected, value) {
      this.$set(this.hidden, toBeAffected.key, this.shouldChange(toBeAffected.change, value));
    },

    shouldChange(change, value) {
      if (Object.keys(change).includes('when')) {
        if (Array.isArray(change.when)) {
          return change.when.includes(value);
        }

        if (typeof change.when == 'function') {
          return change.when(value);
        }

        return change.when == value;
      }

      if (Object.keys(change).includes('whenNot')) {
        if (Array.isArray(change.whenNot)) {
          return !change.whenNot.includes(value);
        }

        if (typeof change.whenNot == 'function') {
          return change.whenNot(value);
        }

        return change.whenNot != value;
      }

      return false;
    },

    handleClear(toBeAffected) {
      this.$set(this.formObject, toBeAffected.key, '');
    },

    handleOrder(toBeAffected, value) {
      this.reactivePropertyChange(toBeAffected, value, 'order');
    },

    handleDisable(toBeAffected, value) {
      if (toBeAffected.change.affectees) {
        var that = this;
        toBeAffected.change.affectees.forEach(function (key) {
          that.$set(that.disabled, key, that.shouldChange(toBeAffected.change, value));
        });
      } else {
        this.$set(this.disabled, toBeAffected.key, this.shouldChange(toBeAffected.change, value));
      }
    },

    handleEnable(toBeAffected, value) {
      if (toBeAffected.change.affectees) {
        var that = this;
        toBeAffected.change.affectees.forEach(function (key) {
          that.$set(that.disabled, key, !that.shouldChange(toBeAffected.change, value));
        });
      } else {
        this.$set(this.disabled, toBeAffected.key, !this.shouldChange(toBeAffected.change, value));
      }
    },

    handleCols(toBeAffected, value) {
      this.reactivePropertyChange(toBeAffected, value, 'cols');
    },

    reactivePropertyChange(toBeAffected, value, property) {
      let elementToChangeOrderTo = this.localFormElements.find(x => x.key == toBeAffected.key);
      let index = this.localFormElements.findIndex(x => x.key == toBeAffected.key);
      let affectedValue = toBeAffected.change.bindings[value];

      if (affectedValue) {
        elementToChangeOrderTo[property] = affectedValue;
      } else {
        elementToChangeOrderTo[property] = this.initialOrderings[toBeAffected.key];
      }

      this.$set(this.localFormElements, index, elementToChangeOrderTo);
    },

    handleValue(toBeAffected, value) {
      if (toBeAffected.change.affectees) {
        for (let key of toBeAffected.change.affectees) {
          this.changeValue(toBeAffected, value, key);
        }
      } else {
        this.changeValue(toBeAffected, value, toBeAffected.key);
      }
    },

    changeValue(toBeAffected, value, key) {
      const affectee = this.formElements.filter(x => x.key == key)[0];

      if (!affectee) {
        throw new Error(`There was an error affecting the value. Used key ${key} did not resolve to any input element.`);
      }

      const binding = toBeAffected.change.bindings[value];

      if (toBeAffected.change.bindings && Object.keys(toBeAffected.change.bindings).includes(typeof value == 'string' ? value : String(value))) {
        this.$set(this.formObject, key, binding);
      } else {
        if (toBeAffected.change.bindings && Object.keys(toBeAffected.change.bindings).includes('$any')) {
          if (value != undefined) {
            this.$set(this.formObject, key, toBeAffected.change.bindings['$any']);
          } else {
            this.$set(this.formObject, key, undefined);
          }
        }
      }
    },

    handleDataSourceChange: async function (toBeAffected, value) {
      var affectee = this.formElements.filter(x => x.key == toBeAffected.key)[0];

      if (!affectee) {
        throw new Error(`There was an error affecting the DataSource. Used key ${toBeAffected.key} did not resolve to any input element.`);
      }

      let specificBindingMatched = toBeAffected.change.bindings && toBeAffected.change.bindings[value];

      if (specificBindingMatched) {
        let binding = toBeAffected.change.bindings[value];

        if (Array.isArray(binding)) {
          var _affectee$props;

          binding = (_affectee$props = affectee.props) !== null && _affectee$props !== void 0 && _affectee$props.multiple ? binding : dataSourceBuilder.addFirstOption(binding);
          this.$set(this.dataSources, toBeAffected.key, binding);
          let selected = binding.find(x => x.selected);

          if (selected) {
            this.formObject[toBeAffected.key] = selected.value;
          }

          return;
        }

        if (affectee.props && affectee.props.multiple) {
          binding.props = {
            multiple: true
          };
        }

        if (binding.api) {
          this.$set(this.dataSources, toBeAffected.key, await dataSourceBuilder.buildDataSource(binding));
          return;
        }

        throw new Error('DataSource binding must contain either new DataSource or an api endpoint.');
      }

      if (toBeAffected.change.api) {
        let dataSource = {
          dataSource: {
            endpoint: toBeAffected.change.api.endpoint
          }
        };

        if (affectee.props && affectee.props.multiple) {
          dataSource.props = {
            multiple: true
          };
        }

        if (toBeAffected.change.api.associateValue) {
          if (value === null || value === undefined) {
            //clearing queryString since no item is selected/checked
            dataSource.dataSource.endpoint = dataSource.dataSource.endpoint.split('&')[dataSource.dataSource.endpoint.split('&').length - 2];
          } else {
            if (Array.isArray(value)) {
              var checkedItems = [];

              for (let item of value) {
                if (item !== null && item !== undefined) {
                  checkedItems.push(item);
                }
              }

              if (checkedItems.length) {
                dataSource.dataSource.endpoint += checkedItems;
              } else {
                dataSource.dataSource.endpoint = dataSource.dataSource.endpoint.split('&')[dataSource.dataSource.endpoint.split('&').length - 2];
              }
            } else {
              if (value != null && value != undefined) {
                dataSource.dataSource.endpoint += value;
              }
            }
          }
        }

        this.$set(this.dataSources, toBeAffected.key, await dataSourceBuilder.buildDataSource(dataSource));
      }
    }
  }
};

var formBuilderMethods = {
  methods: {
    onEnter: function () {
      if (this.submitOnEnter) {
        this.performSubmit();
      }
    },
    component: function (formElement) {
      return formElement.component ? formElement.component : 'v-text-field';
    },
    hint: function (formElement) {
      if (!formElement.hint) {
        return undefined;
      }

      if (this.shouldBeTranslated(formElement.hint)) {
        return this.translate(formElement.hint);
      }

      return formElement.hint;
    },
    mDense: function (formElement) {
      if (Object.keys(formElement).includes('dense')) {
        return formElement.dense;
      }

      return this.dense;
    },
    label: function (formElement) {
      if (formElement.label) {
        if (this.shouldBeTranslated(formElement.label)) {
          return this.translate(formElement.label, true);
        }

        return formElement.label;
      }

      return this.toSentenceCase(formElement.key);
    },
    type: function (formElement) {
      return formElement.type ? formElement.type : undefined;
    },
    rules: function (formElement) {
      if (formElement.rules) {
        return formElement.rules;
      }

      return '';
    },
    cols: function (formElement) {
      if (!Object.keys(formElement).includes('cols')) {
        return 12;
      }

      if (typeof formElement.cols == 'object') {
        let currentScreen = this.$vuetify.breakpoint.name;
        let exactColMatch = formElement.cols[currentScreen];

        if (exactColMatch) {
          return exactColMatch;
        }

        let sizes = ['xs', 'sm', 'md', 'lg', 'xl'];
        let currentScreenIndex = sizes.findIndex(x => x == currentScreen);

        for (let i = currentScreenIndex - 1; i >= 0; i--) {
          let lowerSizeDefinition = formElement.cols[sizes[i]];

          if (lowerSizeDefinition) {
            return lowerSizeDefinition;
          }
        }

        return 12;
      }

      return formElement.cols;
    },
    offset: function (formElement) {
      return formElement.offset ? formElement.offset : 0;
    },
    mColor: function (formElement) {
      return formElement.color ? formElement.color : this.color;
    },
    mClearable: function (formElement) {
      return formElement.clearable ? formElement.clearable : this.clearableInputs;
    },

    performSubmit() {
      this.$refs.observer.validate().then(valid => {
        if (valid) {
          if (this.additionalValidation) {
            let errors = this.additionalValidation(this.formObject);

            if (Object.keys(errors).length) {
              this.$refs.observer.setErrors(errors);
              return;
            }
          }

          let objectToSubmit = {};

          for (let prop in this.formObject) {
            if ((!this.hidden[prop] || this.find(prop).sendOnHidden) && this.formObject[prop] !== undefined && this.formObject[prop] !== null && this.formObject[prop].toString().trim() !== '') {
              if (prop.includes('_upload')) {
                let originalPropName = prop.split('_upload')[0];
                objectToSubmit[originalPropName] = this.formObject[prop];
              } else {
                objectToSubmit[prop] = this.formObject[prop];
              }
            }
          }

          try {
            this.$emit('formSubmit', objectToSubmit);
          } catch (e) {
            throw new Error('There was an error calling a provided function in FormBuilder', e);
          }
        }
      });
    },

    reset() {
      this.$refs.observer.reset();

      for (let prop in this.formObject) {
        if (this.incommingObject && this.incommingObject[prop]) {
          this.formObject[prop] = this.incommingObject[prop];
        } else {
          this.formObject[prop] = '';
        }
      }

      this.$emit('formReset');
    },

    find(key) {
      return this.formElements.filter(x => x.key == key)[0];
    }

  }
};

function errorHandler(propValue, fn) {
  let errors = [];
  fn(propValue, errors);
  errors.forEach(e => {
    if (e.isWarning) {
      console.warn(e.error);
    } else {
      throw new Error(e.error);
    }
  });
  return errors.filter(x => !x.isWarning).length === 0;
}

function formElements(propValue, errors) {
  propValue.forEach(x => {
    if (!x.key) {
      errors.push({
        error: "Each object of formElements array must have defined 'key' property."
      });
    }
  });
  return errors;
}

function cancel(propValue, errors) {
  if (!propValue.text) {
    errors.push({
      isWarning: true,
      error: "Prop 'cancel' should have defined 'text' property."
    });
  }

  if (!propValue.color) {
    errors.push({
      isWarning: true,
      error: "Prop 'cancel' should have defined 'color' property. Using white..."
    });
  }
}

function submit(propValue, errors) {
  if (!propValue.text) {
    errors.push({
      isWarning: true,
      error: "Prop 'submit' should have defined 'text' property."
    });
  }

  if (!propValue.color) {
    errors.push({
      isWarning: true,
      error: "Prop 'submit' should have defined 'color' property. Using white..."
    });
  }
}

var propValidation = {
  formElements: prop => errorHandler(prop, formElements),
  cancel: prop => errorHandler(prop, cancel),
  submit: prop => errorHandler(prop, submit)
};

var formBuilderProps = {
  formElements: {
    type: Array,
    required: true,
    validator: propValidation.formElements
  },
  loading: {
    type: Boolean,

    default() {
      return false;
    }

  },
  errors: {
    type: Object,
    required: false
  },
  incommingObject: {
    type: Object,
    required: false
  },
  color: {
    type: String,
    default: 'primary'
  },
  submit: {
    type: Object,
    required: false,
    default: function () {
      return {
        text: '$submit',
        color: 'success'
      };
    },
    validator: propValidation.submit
  },
  useCancel: {
    type: Boolean,
    default: true
  },
  cancel: {
    type: Object,
    required: false,
    default: function () {
      return {
        text: '$cancel',
        color: 'warning'
      };
    },
    validator: propValidation.cancel
  },
  additionalValidation: {
    type: Function,
    required: false
  },
  clearableInputs: {
    type: Boolean,
    default: false
  },
  dense: {
    type: Boolean,
    default: true
  },
  submitOnEnter: {
    type: Boolean,
    default: true
  },
  inline: {
    type: Boolean,
    default: false
  }
};

var localizationMixin = {
  methods: {
    shouldBeTranslated(keyword) {
      return keyword.indexOf('$') == 0;
    },

    translate(translatable, upperFirst) {
      if (!this.shouldBeTranslated(translatable)) {
        return upperFirst ? this.capitalize(translatable) : translatable;
      }

      const keyToTranslate = translatable.substring(1);

      if (!this.$t) {
        console.warn(`Cant translate ${keyToTranslate}, no i18n translator found.`);
        return keyToTranslate;
      }

      return upperFirst ? this.capitalize(this.$t(keyToTranslate)) : this.$t(keyToTranslate);
    },

    capitalize(s) {
      if (typeof s !== 'string') return '';
      return s.charAt(0).toUpperCase() + s.slice(1);
    },

    canTranslate(keyword) {
      return this.$te(keyword);
    }

  }
};

//
Vue.component('datepicker', VueCtkDateTimePicker);
var script$5 = {
  name: 'FormBuilder',
  mixins: [formBuilderMixin, localizationMixin, formBuilderMethods],
  props: formBuilderProps,
  computed: {
    formElementsOrdered() {
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      return this.localFormElements.sort((x, y) => {
        if (x.order > y.order) return 1;
        return x.order < y.order ? -1 : 0;
      });
    }

  },
  data: function () {
    return {
      formIsValid: true,
      date: '',
      formObject: {},
      dataSources: {},
      hidden: {},
      disabled: {},
      localFormElements: [],
      initialOrderings: {},
      initialCols: {}
    };
  },
  mounted: async function () {
    if (!this.$formBuilderAxios) {
      console.warn('Axios not found on a vue instance. You will not be able to use AJAX based features.');
    }

    this.formElements.forEach(x => {
      if (!x.order) {
        x.order = 999;
      }

      this.localFormElements.push(x);
    });
    this.localFormElements.forEach(x => {
      this.$set(this.initialOrderings, x.key, x.order);
      this.$set(this.initialCols, x.key, x.cols);
    });
    await this.prepareFormObject();
  },
  watch: {
    errors: {
      handler() {
        this.$refs.observer.setErrors(this.errors);
      }

    },
    incommingObject: function () {
      this.populateValuesBasedOnIncommingObject();
    }
  }
};

function normalizeComponent(template, style, script, scopeId, isFunctionalTemplate, moduleIdentifier /* server only */, shadowMode, createInjector, createInjectorSSR, createInjectorShadow) {
    if (typeof shadowMode !== 'boolean') {
        createInjectorSSR = createInjector;
        createInjector = shadowMode;
        shadowMode = false;
    }
    // Vue.extend constructor export interop.
    const options = typeof script === 'function' ? script.options : script;
    // render functions
    if (template && template.render) {
        options.render = template.render;
        options.staticRenderFns = template.staticRenderFns;
        options._compiled = true;
        // functional template
        if (isFunctionalTemplate) {
            options.functional = true;
        }
    }
    // scopedId
    if (scopeId) {
        options._scopeId = scopeId;
    }
    let hook;
    if (moduleIdentifier) {
        // server build
        hook = function (context) {
            // 2.3 injection
            context =
                context || // cached call
                    (this.$vnode && this.$vnode.ssrContext) || // stateful
                    (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext); // functional
            // 2.2 with runInNewContext: true
            if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
                context = __VUE_SSR_CONTEXT__;
            }
            // inject component styles
            if (style) {
                style.call(this, createInjectorSSR(context));
            }
            // register component module identifier for async chunk inference
            if (context && context._registeredComponents) {
                context._registeredComponents.add(moduleIdentifier);
            }
        };
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        options._ssrRegister = hook;
    }
    else if (style) {
        hook = shadowMode
            ? function (context) {
                style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot));
            }
            : function (context) {
                style.call(this, createInjector(context));
            };
    }
    if (hook) {
        if (options.functional) {
            // register for functional component in vue file
            const originalRender = options.render;
            options.render = function renderWithStyleInjection(h, context) {
                hook.call(context);
                return originalRender(h, context);
            };
        }
        else {
            // inject component registration as beforeCreate hook
            const existing = options.beforeCreate;
            options.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
    }
    return script;
}

/* script */
const __vue_script__$5 = script$5;
/* template */

var __vue_render__$5 = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('ValidationObserver', {
    ref: "observer"
  }, [_c('v-row', {
    class: _vm.inline ? 'justify-end' : 'justify-start'
  }, [_vm._l(_vm.formElementsOrdered, function (formElement) {
    return [!_vm.hidden[formElement.key] ? _c('v-col', {
      key: formElement.key,
      staticClass: "form-builder-input",
      attrs: {
        "cols": _vm.cols(formElement),
        "offset": _vm.offset(formElement)
      }
    }, [!_vm.hidden[formElement.key] ? _c('ValidationProvider', {
      key: formElement.key,
      attrs: {
        "vid": formElement.key,
        "name": formElement.key,
        "rules": _vm.rules(formElement)
      },
      scopedSlots: _vm._u([{
        key: "default",
        fn: function (ref) {
          var errors = ref.errors;
          return [_vm.component(formElement) == 'datepicker' ? _c('datepicker', _vm._b({
            attrs: {
              "no-value-to-custom-elem": true
            },
            model: {
              value: _vm.formObject[formElement.key],
              callback: function ($$v) {
                _vm.$set(_vm.formObject, formElement.key, $$v);
              },
              expression: "formObject[formElement.key]"
            }
          }, 'datepicker', formElement.props, false), [_c('v-text-field', _vm._b({
            key: formElement.key,
            ref: formElement.key,
            refInFor: true,
            attrs: {
              "disabled": _vm.disabled[formElement.key],
              "value": _vm.formObject[formElement.key],
              "hint": _vm.hint(formElement),
              "label": _vm.label(formElement),
              "outlined": "",
              "hide-details": true,
              "error-messages": errors,
              "dense": _vm.mDense(formElement),
              "color": _vm.mColor(formElement),
              "clearable": _vm.mClearable(formElement),
              "persistent-hint": ""
            },
            on: {
              "change": function ($event) {
                return _vm.handleChange(formElement.key, $event, true);
              }
            }
          }, 'v-text-field', formElement.props, false))], 1) : _vm._e(), _vm._v(" "), _vm.component(formElement) != 'datepicker' ? _c(_vm.component(formElement), _vm._b({
            key: formElement.key,
            ref: formElement.key,
            refInFor: true,
            tag: "component",
            attrs: {
              "disabled": _vm.disabled[formElement.key],
              "hint": _vm.hint(formElement),
              "label": _vm.label(formElement),
              "type": _vm.type(formElement),
              "items": _vm.dataSources[formElement.key],
              "outlined": "",
              "hide-details": "auto",
              "error-messages": errors,
              "src": _vm.formObject[formElement.key],
              "dense": _vm.mDense(formElement),
              "color": _vm.mColor(formElement),
              "clearable": _vm.mClearable(formElement),
              "persistent-hint": ""
            },
            on: {
              "change": function ($event) {
                return _vm.handleChange(formElement.key, $event);
              },
              "input": function ($event) {
                return _vm.handleChange(formElement.key, $event);
              },
              "keyup": function ($event) {
                if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")) {
                  return null;
                }

                return _vm.onEnter.apply(null, arguments);
              }
            },
            model: {
              value: _vm.formObject[formElement.key],
              callback: function ($$v) {
                _vm.$set(_vm.formObject, formElement.key, $$v);
              },
              expression: "formObject[formElement.key]"
            }
          }, 'component', formElement.props, false)) : _vm._e()];
        }
      }], null, true)
    }) : _vm._e()], 1) : _vm._e()];
  }), _vm._v(" "), _vm._t("default"), _vm._v(" "), _c('v-col', {
    staticClass: "form-builder-input",
    attrs: {
      "cols": _vm.inline ? 1 : 12
    }
  }, [_c('span', {
    class: _vm.inline ? '' : 'float-right'
  }, [_vm.useCancel ? _c('v-btn', {
    attrs: {
      "id": "formSubmitCancel",
      "small": "",
      "disabled": _vm.loading,
      "color": _vm.cancel.color
    },
    on: {
      "click": _vm.reset
    }
  }, [_vm._v(_vm._s(_vm.translate(_vm.cancel.text, true)))]) : _vm._e(), _vm._v(" "), _c('v-btn', {
    staticClass: "ml-2",
    attrs: {
      "id": "formBuilderSubmit",
      "disabled": _vm.loading || !_vm.formIsValid,
      "loading": _vm.loading,
      "small": "",
      "color": _vm.submit.color
    },
    on: {
      "click": _vm.performSubmit
    }
  }, [_vm._v("\n          " + _vm._s(_vm.translate(_vm.submit.text, true)) + "\n        ")])], 1)])], 2)], 1);
};

var __vue_staticRenderFns__$5 = [];
/* style */

const __vue_inject_styles__$5 = undefined;
/* scoped */

const __vue_scope_id__$5 = undefined;
/* module identifier */

const __vue_module_identifier__$5 = undefined;
/* functional template */

const __vue_is_functional_template__$5 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$5 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$5,
  staticRenderFns: __vue_staticRenderFns__$5
}, __vue_inject_styles__$5, __vue_script__$5, __vue_scope_id__$5, __vue_is_functional_template__$5, __vue_module_identifier__$5, false, undefined, undefined, undefined);

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
var script$4 = {
  name: 'ApiForm',
  data: function () {
    return {
      snackbar: false,
      snackbarColor: 'success',
      snackbarText: 'Successfull insert.',
      validationErrors: {},
      loading: false
    };
  },
  props: {
    formElements: {
      type: Array,
      required: true
    },
    endpoint: {
      type: String,
      required: true
    },
    queryParams: {
      type: Object,
      required: false
    },
    color: {
      type: String,
      required: false
    },
    method: {
      type: String,
      default: 'POST'
    },
    validationErrorResponseCode: {
      type: Number,
      default: 422
    },
    formObject: {
      type: Object,
      required: false
    },
    submit: {
      type: Object,
      required: false
    },
    cancel: {
      type: Object,
      required: false
    },
    useCancel: {
      type: Boolean,
      default: true
    },
    dense: {
      type: Boolean,
      default: true
    },
    contentType: {
      type: String,
      default: 'application/json'
    },
    successFn: {
      type: Function,
      required: false
    },
    errorFn: {
      type: Function,
      required: false
    },
    extractErrorsFn: {
      type: Function,
      required: false
    },
    validationErrorsProperty: {
      type: String,
      required: false
    },
    success: {
      type: Object,
      default: function () {
        return {
          color: 'success',
          message: 'An action executed successfully.'
        };
      }
    },
    error: {
      type: Object,
      default: function () {
        return {
          color: 'red',
          message: 'An error has occured. Please contact administrator.'
        };
      }
    },
    submitOnEnter: {
      type: Boolean,
      default: true
    },
    submitOnLoad: {
      type: Boolean,
      default: false
    },
    inline: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    callApi() {
      this.$refs.formBuilder.performSubmit();
    },

    handleFormReset() {
      this.$emit('formReset');
    },

    resetForm() {
      this.$refs.formBuilder.reset();
    },

    handleSubmit(formObject) {
      this.loading = true;

      if (this.contentType == 'multipart/form-data') {
        formObject = this.getMultipartObject(formObject);
      }

      console.log(formObject);
      let request = {
        method: this.method,
        url: this.endpoint,
        data: formObject,
        headers: {
          'Content-Type': this.contentType
        },
        params: this.method == 'GET' ? { ...formObject,
          ...this.queryParams
        } : this.queryParams,
        paramsSerializer: function (params) {
          let qs = '';

          for (let param in params) {
            if (params[param] == '') {
              continue;
            }

            if (Array.isArray(params[param])) {
              for (let item of params[param]) {
                qs += `${param}=${item}&`;
              }
            } else {
              qs += `${param}=${params[param]}&`;
            }
          }

          console.log(qs);
          return qs;
        }
      };
      this.$formBuilderAxios(request).then(response => {
        this.loading = false;

        if (this.successFn) {
          this.successFn(response.data);
        } else {
          this.snackbarColor = this.success.color;
          this.snackbar = true;
          this.snackbarText = this.success.message;
        }
      }).catch(error => {
        this.loading = false;

        if (error.response.status == this.validationErrorResponseCode) {
          if (this.extractErrorsFn) {
            this.setErrors(this.extractErrorsFn(error.response.data));
          } else {
            if (this.validationErrorsProperty) {
              const properties = this.validationErrorsProperty.split('.');
              let tempData = error.response.data;
              properties.forEach(p => {
                tempData = tempData[p];
              });
              this.setErrors(tempData);
            } else {
              this.setErrors(error.response.data);
            }
          }
        } else {
          if (this.errorFn) {
            this.errorFn(error.response);
          } else {
            this.snackbar = true;
            this.snackbarColor = this.error.color;
            this.snackbarText = this.error.message;
          }
        }
      });
    },

    setErrors(errors) {
      this.validationErrors = {};

      for (let error of errors) {
        this.$set(this.validationErrors, error.propertyName, [error.error]);
      }
    },

    getMultipartObject(formObject) {
      let formData = new FormData();

      for (let prop in formObject) {
        if (formObject[prop].isFormData) {
          for (let item of formObject[prop].items) {
            formData.append(prop, item);
          }
        } else {
          formData.append(prop, formObject[prop]);
        }
      }

      return formData;
    }

  }
};

/* script */
const __vue_script__$4 = script$4;
/* template */

var __vue_render__$4 = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('div', [_c('FormBuilder', {
    ref: "formBuilder",
    attrs: {
      "formElements": _vm.formElements,
      "errors": _vm.validationErrors,
      "incommingObject": _vm.formObject,
      "submit": _vm.submit,
      "cancel": _vm.cancel,
      "useCancel": _vm.useCancel,
      "dense": _vm.dense,
      "color": _vm.color,
      "submitOnEnter": _vm.submitOnEnter,
      "submitOnLoad": _vm.submitOnLoad,
      "loading": _vm.loading,
      "inline": _vm.inline
    },
    on: {
      "formSubmit": _vm.handleSubmit,
      "formReset": _vm.handleFormReset
    }
  }), _vm._v(" "), _vm.snackbar ? _c('v-snackbar', {
    attrs: {
      "right": "",
      "color": _vm.snackbarColor
    },
    scopedSlots: _vm._u([{
      key: "action",
      fn: function (ref) {
        var attrs = ref.attrs;
        return [_c('v-btn', _vm._b({
          attrs: {
            "color": "white",
            "text": ""
          },
          on: {
            "click": function ($event) {
              _vm.snackbar = false;
            }
          }
        }, 'v-btn', attrs, false), [_vm._v("\n        Close\n      ")])];
      }
    }], null, false, 4217558357),
    model: {
      value: _vm.snackbar,
      callback: function ($$v) {
        _vm.snackbar = $$v;
      },
      expression: "snackbar"
    }
  }, [_vm._v("\n    " + _vm._s(_vm.snackbarText) + "\n    ")]) : _vm._e()], 1);
};

var __vue_staticRenderFns__$4 = [];
/* style */

const __vue_inject_styles__$4 = undefined;
/* scoped */

const __vue_scope_id__$4 = undefined;
/* module identifier */

const __vue_module_identifier__$4 = undefined;
/* functional template */

const __vue_is_functional_template__$4 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$4 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$4,
  staticRenderFns: __vue_staticRenderFns__$4
}, __vue_inject_styles__$4, __vue_script__$4, __vue_scope_id__$4, __vue_is_functional_template__$4, __vue_module_identifier__$4, false, undefined, undefined, undefined);

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
var script$3 = {
  name: 'SearchForm',
  props: {
    formElements: {
      type: Array,
      required: true
    },
    color: {
      type: String,
      required: false
    },
    endpoint: {
      type: String,
      required: true
    },
    method: {
      type: String,
      default: 'GET'
    },
    submit: {
      type: Object,
      default: function () {
        return {
          text: 'Search',
          color: 'primary'
        };
      }
    },
    cancel: {
      type: Object,
      required: false
    },
    useCancel: {
      type: Boolean,
      default: false
    },
    dense: {
      type: Boolean,
      default: true
    },
    submitOnEnter: {
      type: Boolean,
      default: true
    },
    searchOnLoad: {
      type: Boolean,
      default: false
    },
    queryParams: {
      type: Object,
      required: false
    },
    inline: {
      type: Boolean,
      default: false
    }
  },

  mounted() {
    if (this.searchOnLoad) {
      this.executeSearch();
    }
  },

  methods: {
    resultHandler: function (apiData) {
      this.$emit('success', apiData);
    },
    errorHandler: function (apiError) {
      this.$emit('error', apiError);
    },
    executeSearch: function () {
      this.$refs.apiForm.callApi();
    }
  }
};

/* script */
const __vue_script__$3 = script$3;
/* template */

var __vue_render__$3 = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('ApiForm', {
    ref: "apiForm",
    attrs: {
      "formElements": _vm.formElements,
      "endpoint": _vm.endpoint,
      "method": _vm.method,
      "useCancel": _vm.useCancel,
      "cancel": _vm.cancel,
      "submit": _vm.submit,
      "dense": _vm.dense,
      "color": _vm.color,
      "successFn": _vm.resultHandler,
      "errorFn": _vm.errorHandler,
      "submitOnEnter": _vm.submitOnEnter,
      "queryParams": _vm.queryParams,
      "inline": _vm.inline
    }
  });
};

var __vue_staticRenderFns__$3 = [];
/* style */

const __vue_inject_styles__$3 = undefined;
/* scoped */

const __vue_scope_id__$3 = undefined;
/* module identifier */

const __vue_module_identifier__$3 = undefined;
/* functional template */

const __vue_is_functional_template__$3 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$3 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$3,
  staticRenderFns: __vue_staticRenderFns__$3
}, __vue_inject_styles__$3, __vue_script__$3, __vue_scope_id__$3, __vue_is_functional_template__$3, __vue_module_identifier__$3, false, undefined, undefined, undefined);

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
var script$2 = {
  name: 'RestUpdateForm',
  props: {
    formElements: {
      type: Array,
      required: true
    },
    resource: {
      type: String,
      required: true
    },
    id: {
      type: Number,
      required: true
    },
    color: {
      type: String,
      required: false
    },
    method: {
      type: String,
      default: 'PUT'
    },
    submit: {
      type: Object,
      default: function () {
        return {
          text: '$update',
          color: 'primary'
        };
      }
    },
    cancel: {
      type: Object,
      required: false
    },
    useCancel: {
      type: Boolean,
      default: true
    },
    dense: {
      type: Boolean,
      default: true
    },
    submitOnEnter: {
      type: Boolean,
      default: true
    },
    extractErrorsFn: {
      type: Function,
      required: false
    },
    validationErrorsProperty: {
      type: String,
      required: false
    }
  },

  data() {
    return {
      formObject: null
    };
  },

  async beforeMount() {
    var response = await this.$formBuilderAxios.get(this.endpoint);
    this.formObject = response.data;
  },

  computed: {
    endpoint: function () {
      return `${this.resource}/${this.id}`;
    }
  },
  methods: {
    resultHandler: function (apiData) {
      this.$emit('success', apiData);
    },
    errorHandler: function (apiError) {
      this.$emit('error', apiError);
    },

    handleFormReset() {
      this.$emit('formReset');
    }

  },
  watch: {
    id: async function () {
      var response = await this.$formBuilderAxios.get(this.endpoint);
      this.formObject = response.data;
    }
  }
};

/* script */
const __vue_script__$2 = script$2;
/* template */

var __vue_render__$2 = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _vm.formObject ? _c('ApiForm', {
    attrs: {
      "formElements": _vm.formElements,
      "endpoint": _vm.endpoint,
      "method": _vm.method,
      "useCancel": _vm.useCancel,
      "cancel": _vm.cancel,
      "submit": _vm.submit,
      "dense": _vm.dense,
      "color": _vm.color,
      "successFn": _vm.resultHandler,
      "errorFn": _vm.errorHandler,
      "formObject": _vm.formObject,
      "submitOnEnter": _vm.submitOnEnter,
      "extractErrorsFn": _vm.extractErrorsFn,
      "validationErrorsProperty": _vm.validationErrorsProperty
    },
    on: {
      "formReset": _vm.handleFormReset
    }
  }) : _vm._e();
};

var __vue_staticRenderFns__$2 = [];
/* style */

const __vue_inject_styles__$2 = undefined;
/* scoped */

const __vue_scope_id__$2 = undefined;
/* module identifier */

const __vue_module_identifier__$2 = undefined;
/* functional template */

const __vue_is_functional_template__$2 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$2,
  staticRenderFns: __vue_staticRenderFns__$2
}, __vue_inject_styles__$2, __vue_script__$2, __vue_scope_id__$2, __vue_is_functional_template__$2, __vue_module_identifier__$2, false, undefined, undefined, undefined);

let props$1 = {
  tableProps: {
    type: Object,
    default: function () {
      return {};
    }
  },
  filters: {
    type: Array,
    required: true
  },
  searchSubmit: {
    type: Object,
    default: function () {
      return {
        text: 'Search',
        color: 'primary'
      };
    }
  },
  headerColor: {
    type: String,
    required: false
  },
  specializedColumns: {
    type: Array,
    required: false
  },
  formatters: {
    type: Object,
    required: false
  },
  searchTitle: {
    type: String,
    default: 'Search'
  },
  filterPosition: {
    type: String,
    default: 'right',
    validator: function (value) {
      return ['left', 'right', 'top'].includes(value);
    }
  },
  api: {
    type: Object,
    required: true,
    validator: function (value) {
      let errors = [];

      if (!value.endpoint) {
        errors.push('endpoint is required property.');
      }

      errors.forEach(e => console.warn(e));
      return !errors.length;
    }
  },
  headers: {
    type: Array,
    required: false,
    validator: function (value) {
      return !value.some(x => !x.text || !x.value);
    }
  },
  excludedHeaders: {
    type: Array,
    required: false,
    default: function () {
      return [];
    }
  },
  columnWidth: {
    type: String | Number,
    required: false,
    default: 'auto',
    validator: function (value) {
      if (!isNaN(value)) {
        return true;
      }

      return ['fit', 'auto'].includes(value);
    }
  },
  pagingSortingQsBuilder: {
    type: Function,
    default: function (options) {
      return defaultQueryStringBuilder$1(options);
    }
  },
  dataExtraction: {
    type: Object | Function,
    default: function () {
      return {
        dataProperty: undefined,
        totalItemsProperty: undefined
      };
    }
  },
  initialQueryParams: {
    required: false,
    default: function () {
      return {};
    }
  },
  processDataOn: {
    type: String,
    default: 'client',
    validator: function (value) {
      return ['client', 'server', 'mixed'].includes(value);
    }
  },
  formFieldsColor: {
    type: String,

    default() {
      return 'primary';
    }

  },
  inlineSearch: {
    type: Boolean,
    default: false
  },
  itemsPerPageText: {
    type: String,
    default: 'Rows per page'
  },
  rowActions: {
    type: Boolean,
    default: false
  },
  useCustomDialog: {
    type: Boolean,
    default: false
  },
  itemsPerPageOptions: {
    type: Array,
    required: false
  }
};

function toSentenceCase$1(text) {
  var result = text.replace(/([A-Z])/g, ' $1');
  return result.charAt(0).toUpperCase() + result.slice(1);
}

function makeHeadersFromFirstItem$1(item, headersToExclude, table) {
  return Object.keys(item).filter(x => !headersToExclude.includes(x)).map(key => {
    return {
      text: table.canTranslate(key) ? table.translate('$' + key) : toSentenceCase$1(key),
      value: key
    };
  });
}

function defaultQueryStringBuilder$1(options) {
  let qs = {
    paginate: true
  };
  qs.perPage = options.perPage;
  qs.page = options.currentPage;

  if (options.sorts.length) {
    qs.sortBy = options.sorts[0].sortBy + '.' + options.sorts[0].direction;

    for (let i = 1; i < options.sorts.length; i++) {
      qs.sortBy += ',' + options.sorts[i].sortBy + '.' + options.sorts[i].direction;
    }
  }

  return qs;
}

var defaults = {
  props: props$1,
  toSentenceCase: toSentenceCase$1,
  makeHeadersFromFirstItem: makeHeadersFromFirstItem$1
};

var apiTableMixin = {
  computed: {
    filtersColOrder() {
      return Number(this.filterPosition == 'right') + 1;
    },

    tableColOrder() {
      return Number(this.filterPosition == 'left') + 1;
    },

    itemsPerPageOptionsComp() {
      return this.itemsPerPageOptions ? this.itemsPerPageOptions : [5, 10, 15, -1];
    },

    tableHeaders() {
      let headers = [];

      if (this.headers) {
        headers = this.headers;
        headers.forEach(x => {
          x.text = this.translate(x.text);
        });
      } else if (this.tableItems.length) {
        headers = defaults.makeHeadersFromFirstItem(this.tableItems[0], this.excludedHeaders, this);
      }

      if (this.useCustomDialog) {
        if (!headers.some(x => x.text == 'Operation')) {
          headers.push({
            text: 'Operation',
            value: 'dialog',
            width: '80',
            sortable: false
          });
        }
      }

      if (this.rowActions) {
        if (!headers.some(x => x.value == 'action')) {
          headers.push({
            text: this.translate('$action'),
            value: 'action',
            width: '80',
            sortable: false
          });
        }
      }

      if (this.tableItems.length) {
        headers.forEach(x => this.setHeaderWidth(x));
      }

      headers.forEach(x => {
        x.class = 'table-header';
      });
      return headers;
    },

    isClientSide() {
      return this.processDataOn == 'client';
    },

    isMixed() {
      return this.processDataOn == 'mixed';
    },

    callApiOnLoad() {
      return this.isMixed || this.isClientSide;
    },

    showSearchForm() {
      return !this.isClientSide;
    },

    filterCols() {
      return this.isClientSide ? 0 : 2;
    },

    tableCols() {
      return !this.isClientSide ? 10 : 12;
    }

  },
  mounted: function () {
    if (this.processDataOn == 'server' && !this.dataExtraction) {
      throw new Error("dataExtraction prop is required when processDataOn is set to 'server'");
    }

    this.initialQueryParamsLocal = this.initialQueryParams;

    if (this.callApiOnLoad) {
      this.$refs.search.executeSearch();
    }
  },
  methods: {
    refreshTable() {
      this.$refs.search.executeSearch();
    },

    processData(data) {
      this.loading = true;
      let tableData = [];

      if (typeof this.dataExtraction == 'function') {
        const converted = this.dataExtraction(data);
        this.totalItems = converted.totalItems;
        tableData = converted.items;
      } else {
        if (this.dataExtraction.dataProperty) {
          const properties = this.dataExtraction.dataProperty.split('.');
          let tempData = data;
          properties.forEach(p => {
            tempData = tempData[p];
          });
          tableData = tempData;
        } else {
          tableData = data;
        }

        this.totalItems = this.isClientSide ? undefined : data[this.dataExtraction.totalItemsProperty];
      }

      if (tableData.length) {
        for (let key in this.formatters) {
          if (Object.keys(tableData[0]).includes(key)) {
            tableData.forEach(x => {
              x[key] = this.formatters[key](x[key]);
            });
          }
        }
      }

      this.tableItems = tableData;
      this.loading = false;
    },

    handleSearchResponse(data) {
      this.processData(data);
    },

    setHeaderWidth: function (header) {
      if (header.width) {
        return;
      }

      if (!isNaN(this.columnWidth)) {
        header.width = this.columnWidth;
        return;
      }

      if (this.columnWidth == 'fit') {
        let firstDataItem = this.tableItems[0];
        let columnWidths = {};

        for (let key of Object.keys(firstDataItem)) {
          columnWidths[key] = pixelWidth(firstDataItem[key], {
            size: 13
          });
        }

        let headerTextWidth = pixelWidth(header.text, {
          size: 13
        });
        header.width = columnWidths[header.value] > headerTextWidth ? columnWidths[header.value] + 50 : headerTextWidth + 50;
      } else {
        this.columnWidth = 'auto';
      }
    }
  }
};

//
var script$1 = {
  name: 'ApiTable',
  props: defaults.props,
  mixins: [apiTableMixin, localizationMixin],

  data() {
    return {
      search: '',
      tableItems: [],
      options: {},
      totalItems: undefined,
      initialQueryParamsLocal: {},
      loading: true
    };
  },

  computed: {
    style() {
      return {
        '--header-color': this.headerColor
      };
    }

  },
  methods: {
    localDateTime(dateTime) {
      var date = DateTime.fromISO(dateTime, {
        locale: Vue.prototype.locale
      });
      date.setLocale('en-GB');
      return date.toLocaleString(DateTime.DATETIME_MED);
    }

  },
  watch: {
    /**
     * On table load, options will change to set the initial page and per page properties
     * That will trigger options watch handler which will in turn trugger the first API call
     */
    options: {
      handler() {
        if (this.isClientSide || this.isMixed) {
          return;
        }

        let sorts = [];

        if (this.options.sortBy.length) {
          for (let i = 0; i < this.options.sortBy.length; i++) {
            sorts.push({
              sortBy: this.options.sortBy[i],
              direction: this.options.sortDesc[i] ? 'desc' : 'asc'
            });
          }
        }

        this.initialQueryParamsLocal = this.pagingSortingQsBuilder({
          perPage: this.options.itemsPerPage,
          currentPage: this.options.page,
          sorts: sorts
        });
        this.$refs.search.executeSearch();
      }

    },
    initialQueryParams: {
      handler() {
        this.initialQueryParamsLocal = this.initialQueryParams;
      }

    }
  }
};

const isOldIE = typeof navigator !== 'undefined' &&
    /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());
function createInjector(context) {
    return (id, style) => addStyle(id, style);
}
let HEAD;
const styles = {};
function addStyle(id, css) {
    const group = isOldIE ? css.media || 'default' : id;
    const style = styles[group] || (styles[group] = { ids: new Set(), styles: [] });
    if (!style.ids.has(id)) {
        style.ids.add(id);
        let code = css.source;
        if (css.map) {
            // https://developer.chrome.com/devtools/docs/javascript-debugging
            // this makes source maps inside style tags work properly in Chrome
            code += '\n/*# sourceURL=' + css.map.sources[0] + ' */';
            // http://stackoverflow.com/a/26603875
            code +=
                '\n/*# sourceMappingURL=data:application/json;base64,' +
                    btoa(unescape(encodeURIComponent(JSON.stringify(css.map)))) +
                    ' */';
        }
        if (!style.element) {
            style.element = document.createElement('style');
            style.element.type = 'text/css';
            if (css.media)
                style.element.setAttribute('media', css.media);
            if (HEAD === undefined) {
                HEAD = document.head || document.getElementsByTagName('head')[0];
            }
            HEAD.appendChild(style.element);
        }
        if ('styleSheet' in style.element) {
            style.styles.push(code);
            style.element.styleSheet.cssText = style.styles
                .filter(Boolean)
                .join('\n');
        }
        else {
            const index = style.ids.size - 1;
            const textNode = document.createTextNode(code);
            const nodes = style.element.childNodes;
            if (nodes[index])
                style.element.removeChild(nodes[index]);
            if (nodes.length)
                style.element.insertBefore(textNode, nodes[index]);
            else
                style.element.appendChild(textNode);
        }
    }
}

/* script */
const __vue_script__$1 = script$1;
/* template */

var __vue_render__$1 = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('v-row', {
    style: _vm.style
  }, [_c('v-col', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: !_vm.isClientSide && _vm.filterPosition != 'top',
      expression: "!isClientSide && filterPosition != 'top'"
    }],
    staticStyle: {
      "padding-bottom": "0px"
    },
    attrs: {
      "cols": "3",
      "xl": "2",
      "order": _vm.filtersColOrder
    }
  }, [_c('v-card', {
    staticClass: "mt-7",
    attrs: {
      "id": "apiTableFilters"
    }
  }, [_c('v-card-title', {
    staticClass: "caption font-weight-bold"
  }, [_vm._v(_vm._s(_vm.translate(_vm.searchTitle)))]), _vm._v(" "), _c('v-card-text', [_c('SearchForm', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: _vm.showSearchForm,
      expression: "showSearchForm"
    }],
    ref: "search",
    staticClass: "mt-2",
    attrs: {
      "color": _vm.formFieldsColor,
      "formElements": _vm.filters,
      "endpoint": _vm.api.endpoint,
      "queryParams": _vm.initialQueryParamsLocal,
      "inline": _vm.filterPosition == 'top',
      "submit": _vm.searchSubmit
    },
    on: {
      "success": _vm.handleSearchResponse
    }
  })], 1)], 1)], 1), _vm._v(" "), _c('v-col', {
    staticStyle: {
      "padding-top": "0px"
    },
    attrs: {
      "cols": _vm.isClientSide || _vm.filterPosition == 'top' ? 12 : 9,
      "xl": _vm.isClientSide || _vm.filterPosition == 'top' ? 12 : 10,
      "order": _vm.tableColOrder
    }
  }, [_c('v-row', {
    staticClass: "pb-3",
    attrs: {
      "no-gutters": ""
    }
  }, [_c('v-col', {
    attrs: {
      "cols": "3",
      "id": "headerSlot"
    }
  }, [_vm._t("header")], 2), _vm._v(" "), _c('v-col', {
    attrs: {
      "cols": "9"
    }
  }, [_vm.isClientSide ? [_c('v-row', {
    attrs: {
      "no-gutters": ""
    }
  }, [_c('v-col', {
    attrs: {
      "cols": "9",
      "offset": "3",
      "md": "4",
      "offset-md": "8"
    }
  }, [_c('v-text-field', {
    staticClass: "shrink",
    attrs: {
      "outlined": "",
      "dense": "",
      "append-icon": "mdi-magnify",
      "label": _vm.translate('$search', true),
      "hide-details": ""
    },
    model: {
      value: _vm.search,
      callback: function ($$v) {
        _vm.search = $$v;
      },
      expression: "search"
    }
  })], 1)], 1)] : _vm._e(), _vm._v(" "), !_vm.isClientSide && _vm.filterPosition == 'top' ? _c('SearchForm', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: _vm.showSearchForm,
      expression: "showSearchForm"
    }],
    ref: "search",
    staticClass: "mr-4",
    attrs: {
      "color": _vm.formFieldsColor,
      "formElements": _vm.filters,
      "endpoint": _vm.api.endpoint,
      "queryParams": _vm.initialQueryParamsLocal,
      "inline": _vm.filterPosition == 'top',
      "submit": _vm.searchSubmit
    },
    on: {
      "success": _vm.handleSearchResponse
    }
  }) : _vm._e()], 2)], 1), _vm._v(" "), _c('v-data-table', _vm._b({
    attrs: {
      "options": _vm.options,
      "search": _vm.search,
      "headers": _vm.tableHeaders,
      "items": _vm.tableItems,
      "loading": _vm.loading,
      "server-items-length": _vm.totalItems,
      "footer-props": {
        'items-per-page-text': _vm.translate('$restTable.perPage'),
        'items-per-page-all-text': _vm.translate('$restTable.allRecords'),
        'items-per-page-options': _vm.itemsPerPageOptionsComp
      }
    },
    on: {
      "update:options": function ($event) {
        _vm.options = $event;
      }
    },
    scopedSlots: _vm._u([_vm._l(_vm.specializedColumns, function (column) {
      return {
        key: "item." + column.property,
        fn: function (ref) {
          var item = ref.item;
          return [column.type == 'image' ? _c('div', {
            key: column.property,
            staticClass: "p-2"
          }, [_c('v-img', {
            attrs: {
              "src": item[column.property],
              "height": "50",
              "width": "50"
            }
          })], 1) : _vm._e(), _vm._v(" "), column.type == 'check' ? [item[column.property] ? _c('v-btn', {
            key: column.property,
            attrs: {
              "text": "",
              "icon": "",
              "color": "blue lighten-2"
            }
          }, [_c('v-icon', [_vm._v("mdi-check")])], 1) : _c('v-btn', {
            key: column.property,
            attrs: {
              "text": "",
              "icon": "",
              "color": "red lighten-2"
            }
          }, [_c('v-icon', [_vm._v("mdi-close")])], 1)] : _vm._e(), _vm._v(" "), column.type == 'date' ? [_vm._v("\n          " + _vm._s(_vm.localDateTime(item[column.property])) + "\n        ")] : _vm._e()];
        }
      };
    }), {
      key: "item.action",
      fn: function (ref) {
        var item = ref.item;
        return [_vm._t("action", null, null, item)];
      }
    }, {
      key: "item.dialog",
      fn: function (ref) {
        var item = ref.item;
        return [_vm._t("dialog", null, null, item)];
      }
    }], null, true)
  }, 'v-data-table', _vm.tableProps, false))], 1)], 1);
};

var __vue_staticRenderFns__$1 = [];
/* style */

const __vue_inject_styles__$1 = function (inject) {
  if (!inject) return;
  inject("data-v-6d354592_0", {
    source: ".table-header{background-color:var(--header-color)}",
    map: undefined,
    media: undefined
  });
};
/* scoped */


const __vue_scope_id__$1 = undefined;
/* module identifier */

const __vue_module_identifier__$1 = undefined;
/* functional template */

const __vue_is_functional_template__$1 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1,
  staticRenderFns: __vue_staticRenderFns__$1
}, __vue_inject_styles__$1, __vue_script__$1, __vue_scope_id__$1, __vue_is_functional_template__$1, __vue_module_identifier__$1, false, createInjector, undefined, undefined);

let props = {
  tableProps: {
    type: Object,
    default: function () {
      return {};
    }
  },
  headerColor: {
    type: String,
    required: false
  },
  modalHeaderColor: {
    type: String,
    required: false
  },
  formFieldsColor: {
    type: String,

    default() {
      return 'primary';
    }

  },
  addBtn: {
    type: Object,

    default() {
      return {
        text: 'Add new',
        color: 'primary'
      };
    }

  },
  customDialog: {
    type: Object,
    required: false
  },
  searchTitle: {
    type: String,
    default: 'Search'
  },
  searchSubmit: {
    type: Object,
    default: function () {
      return {
        text: 'Search',
        color: 'primary'
      };
    }
  },
  formatters: {
    type: Object,
    required: false
  },
  useEdit: {
    type: Boolean,
    default: true
  },
  useInsert: {
    type: Boolean,
    default: true
  },
  persistentDialog: {
    type: Boolean,
    default: false
  },
  insertOpts: {
    type: Object,

    default() {
      return {
        submitText: '$insert',
        submitColor: 'success',
        cancelColor: 'warning',
        cancelText: '$cancel'
      };
    }

  },
  updateOpts: {
    type: Object,

    default() {
      return {
        submitText: '$update',
        submitColor: 'success',
        cancelColor: 'warning',
        cancelText: '$cancel'
      };
    }

  },
  useDelete: {
    type: Boolean,
    default: true
  },
  messages: {
    type: Object,

    default() {
      return {
        deleteConfirmMessage: 'Are you sure you want to delete this item?',
        deleteYes: 'Yes',
        deleteNo: 'No'
      };
    },

    validator(value) {
      let errors = [];

      if (!value.deleteConfirmMessage) {
        errors.push("missing property 'deleteConfirmMessage.'");
      }

      if (!value.deleteYes) {
        errors.push("missing property 'deleteYes.'");
      }

      if (!value.deleteNo) {
        errors.push("missing property 'deleteNo.'");
      }

      errors.forEach(x => {
        throw new Error("Prop 'messages' - " + x);
      });
      return !errors.length;
    }

  },
  resource: {
    type: String,
    required: true
  },
  filters: {
    type: Array,
    required: true
  },
  filterPosition: {
    type: String,
    default: 'right',
    validator: function (value) {
      return ['left', 'right', 'top'].includes(value);
    }
  },
  headers: {
    type: Array,
    required: false,
    validator: function (value) {
      return !value.some(x => !x.text || !x.value);
    }
  },
  excludedHeaders: {
    type: Array,
    required: false,
    default: function () {
      return [];
    }
  },
  columnWidth: {
    type: String | Number,
    required: false,
    default: 'auto',
    validator: function (value) {
      if (!isNaN(value)) {
        return true;
      }

      return ['fit', 'auto'].includes(value);
    }
  },
  loadingMessage: {
    type: String,
    default: 'Loading data...'
  },
  imageColumn: {
    type: String,
    required: false
  },
  pagingSortingQsBuilder: {
    type: Function,
    default: function (options) {
      return defaultQueryStringBuilder(options);
    }
  },
  showDeleteErrors: {
    type: Function,
    required: false
  },
  dataExtraction: {
    type: Object | Function,
    default: function () {
      return {
        dataProperty: undefined,
        totalItemsProperty: undefined
      };
    }
  },
  initialQueryParams: {
    required: false,
    default: function () {
      return {};
    }
  },
  processDataOn: {
    type: String,
    default: 'client',
    validator: function (value) {
      return ['client', 'server', 'mixed'].includes(value);
    }
  },
  inlineSearch: {
    type: Boolean,
    default: false
  },
  itemsPerPageText: {
    type: String,
    default: 'Rows per page'
  },
  rowActions: {
    type: Boolean,
    default: true
  },
  editFormElements: {
    type: Array,
    required: false
  },
  insertFormElements: {
    type: Array,
    required: false
  },
  extractInsertErrors: {
    type: Function,
    required: false
  },
  extractUpdateErrors: {
    type: Function,
    required: false
  },
  specializedColumns: {
    type: Array,
    required: false
  },
  itemsPerPageOptions: {
    type: Array,
    required: false
  }
};

function toSentenceCase(text) {
  var result = text.replace(/([A-Z])/g, ' $1');
  return result.charAt(0).toUpperCase() + result.slice(1);
}

function makeHeadersFromFirstItem(item, headersToExclude, table) {
  return Object.keys(item).filter(x => !headersToExclude.includes(x)).map(key => {
    return {
      text: table.canTranslate(key) ? table.translate('$' + key) : toSentenceCase(key),
      value: key
    };
  });
}

function defaultQueryStringBuilder(options) {
  let qs = {
    paginate: true
  };
  qs.perPage = options.perPage;
  qs.page = options.currentPage;

  if (options.sorts.length) {
    qs.sortBy = options.sorts[0].sortBy + '.' + options.sorts[0].direction;

    for (let i = 1; i < options.sorts.length; i++) {
      qs.sortBy += ',' + options.sorts[i].sortBy + '.' + options.sorts[i].direction;
    }
  }

  return qs;
}

var restTableDefaults = {
  props,
  toSentenceCase,
  makeHeadersFromFirstItem
};

//
var script = {
  name: 'RestTable',
  props: restTableDefaults.props,
  mixins: [localizationMixin],

  data() {
    return {
      dialogDelete: false,
      dialogEdit: false,
      dialogCustom: false,
      toDelete: undefined,
      toUpdate: undefined,
      deleteLoading: false,
      snackbar: false,
      dialogCreate: false,
      customDialogData: {}
    };
  },

  mounted() {
    var that = this;
    this.eventBus.$on('resetTable', function () {
      that.$refs.table.refreshTable();
    });
  },

  computed: {
    fullscreen() {
      return !['lg', 'xl'].includes(this.$vuetify.breakpoint.name);
    },

    insertSubmit() {
      return {
        text: this.insertOpts.submitText,
        color: this.insertOpts.submitColor
      };
    },

    insertCancel() {
      return {
        text: this.insertOpts.cancelText,
        color: this.insertOpts.cancelColor
      };
    },

    updateSubmit() {
      return {
        text: this.updateOpts.submitText,
        color: this.updateOpts.submitColor
      };
    },

    updateCancel() {
      return {
        text: this.updateOpts.cancelText,
        color: this.updateOpts.cancelColor
      };
    }

  },
  methods: {
    closeCreateDialog() {
      this.dialogCreate = false;
      this.$refs.insertForm.resetForm();
    },

    closeCustomDialog() {
      this.dialogCustom = false;
    },

    openCustomDialog(dialogData) {
      this.dialogCustom = true;
      this.customDialogData = dialogData;
    },

    createItem() {
      this.dialogCreate = true;
    },

    editItem(id) {
      this.toUpdate = id;
      this.dialogEdit = true;
    },

    deleteItem(id) {
      this.toDelete = id;
      this.dialogDelete = true;
    },

    closeDelete: function () {
      this.toDelete = undefined, this.dialogDelete = false;
    },
    deleteItemConfirm: async function () {
      await this.$formBuilderAxios.delete(this.resource + `/${this.toDelete}`).then(() => {
        this.toDelete = undefined;
        this.dialogDelete = false;
        this.$refs.table.refreshTable();
      }).catch(error => {
        this.toDelete = undefined;
        this.dialogDelete = false;
        this.$refs.table.refreshTable();

        if (this.showDeleteErrors) {
          this.showDeleteErrors(error.response.data);
        }
      });
    },
    updateSuccess: function () {
      this.dialogEdit = false;
      this.$emit('updateSuccess');
      this.$refs.table.refreshTable();
    },
    updateError: function () {
      this.snackbar = true;
    },
    createSuccess: function () {
      this.dialogCreate = false;
      this.$emit('createSuccess');
      this.$refs.table.refreshTable();
    }
  }
};

/* script */
const __vue_script__ = script;
/* template */

var __vue_render__ = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('div', [_c('ApiTable', _vm._b({
    ref: "table",
    attrs: {
      "api": {
        endpoint: _vm.resource
      },
      "excludedHeaders": _vm.excludedHeaders,
      "useCustomDialog": _vm.customDialog != undefined
    },
    scopedSlots: _vm._u([{
      key: "header",
      fn: function () {
        return [_vm.useInsert ? _c('v-btn', {
          attrs: {
            "color": _vm.addBtn.color,
            "small": ""
          },
          on: {
            "click": _vm.createItem
          }
        }, [_vm._v(_vm._s(_vm.translate(_vm.addBtn.text)))]) : _vm._e(), _vm._v(" "), _vm._t("default")];
      },
      proxy: true
    }, _vm.customDialog ? {
      key: "dialog",
      fn: function (item) {
        return [_c('v-icon', {
          on: {
            "click": function ($event) {
              return _vm.openCustomDialog(item);
            }
          }
        }, [_vm._v("\n        " + _vm._s(_vm.customDialog.icon) + "\n      ")])];
      }
    } : null, _vm.useEdit || _vm.useDelete ? {
      key: "action",
      fn: function (item) {
        return [_vm.useEdit ? _c('v-icon', {
          staticClass: "mr-2",
          attrs: {
            "color": "crud-edit"
          },
          on: {
            "click": function ($event) {
              return _vm.editItem(item.id);
            }
          }
        }, [_vm._v("\n        mdi-pencil\n      ")]) : _vm._e(), _vm._v(" "), _vm.useDelete ? _c('v-icon', {
          attrs: {
            "color": "red"
          },
          on: {
            "click": function ($event) {
              return _vm.deleteItem(item.id);
            }
          }
        }, [_vm._v("\n        mdi-delete\n      ")]) : _vm._e()];
      }
    } : null], null, true)
  }, 'ApiTable', _vm.$props, false)), _vm._v(" "), _vm._t("page-bottom"), _vm._v(" "), _c('v-dialog', {
    attrs: {
      "max-width": "500px"
    },
    model: {
      value: _vm.dialogDelete,
      callback: function ($$v) {
        _vm.dialogDelete = $$v;
      },
      expression: "dialogDelete"
    }
  }, [_c('v-card', [_c('v-toolbar', {
    attrs: {
      "color": _vm.modalHeaderColor
    }
  }, [_c('v-toolbar-title', [_vm._v(_vm._s(_vm.translate('$restTable.delete')))]), _vm._v(" "), _c('v-spacer'), _vm._v(" "), _c('v-btn', {
    attrs: {
      "icon": ""
    }
  }, [_c('v-icon', {
    on: {
      "click": _vm.closeDelete
    }
  }, [_vm._v("mdi-close")])], 1)], 1), _vm._v(" "), _c('v-card-title', {
    staticClass: "headline"
  }, [_vm._v(_vm._s(_vm.translate(_vm.messages.deleteConfirmMessage)))]), _vm._v(" "), _c('v-card-actions', [_c('v-spacer'), _c('v-spacer'), _vm._v(" "), _c('v-btn', {
    attrs: {
      "small": "",
      "id": "restDeleteNo",
      "color": _vm.updateCancel.color
    },
    on: {
      "click": _vm.closeDelete
    }
  }, [_vm._v(_vm._s(_vm.translate(_vm.messages.deleteNo)))]), _vm._v(" "), _c('v-btn', {
    attrs: {
      "small": "",
      "id": "restDeleteYes",
      "color": _vm.updateSubmit.color
    },
    on: {
      "click": _vm.deleteItemConfirm
    }
  }, [_vm._v(_vm._s(_vm.translate(_vm.messages.deleteYes)))])], 1)], 1)], 1), _vm._v(" "), _vm.dialogEdit ? _c('v-dialog', {
    attrs: {
      "max-width": "500px",
      "fullscreen": _vm.fullscreen,
      "persistent": _vm.persistentDialog
    },
    model: {
      value: _vm.dialogEdit,
      callback: function ($$v) {
        _vm.dialogEdit = $$v;
      },
      expression: "dialogEdit"
    }
  }, [_c('v-card', [_c('v-toolbar', {
    attrs: {
      "color": _vm.modalHeaderColor
    }
  }, [_c('v-toolbar-title', [_vm._v(_vm._s(_vm.translate('$restTable.edit')))]), _vm._v(" "), _c('v-spacer'), _vm._v(" "), _c('v-btn', {
    attrs: {
      "icon": ""
    }
  }, [_c('v-icon', {
    on: {
      "click": function ($event) {
        _vm.dialogEdit = false;
      }
    }
  }, [_vm._v("mdi-close")])], 1)], 1), _vm._v(" "), _c('v-card-text', {
    staticClass: "pt-3"
  }, [_c('RestUpdateForm', {
    attrs: {
      "formElements": _vm.editFormElements,
      "resource": _vm.resource,
      "id": _vm.toUpdate,
      "color": _vm.formFieldsColor,
      "submit": _vm.updateSubmit,
      "cancel": _vm.updateCancel,
      "extractErrorsFn": _vm.extractUpdateErrors
    },
    on: {
      "success": _vm.updateSuccess,
      "error": _vm.updateError,
      "formReset": function ($event) {
        _vm.dialogEdit = false;
      }
    }
  })], 1)], 1)], 1) : _vm._e(), _vm._v(" "), _vm.dialogCreate ? _c('v-dialog', {
    attrs: {
      "max-width": "500px",
      "fullscreen": _vm.fullscreen,
      "persistent": _vm.persistentDialog
    },
    model: {
      value: _vm.dialogCreate,
      callback: function ($$v) {
        _vm.dialogCreate = $$v;
      },
      expression: "dialogCreate"
    }
  }, [_c('v-card', [_c('v-toolbar', {
    attrs: {
      "color": _vm.modalHeaderColor
    }
  }, [_c('v-toolbar-title', [_vm._v(_vm._s(_vm.translate('$restTable.create')))]), _vm._v(" "), _c('v-spacer'), _vm._v(" "), _c('v-btn', {
    attrs: {
      "icon": ""
    }
  }, [_c('v-icon', {
    on: {
      "click": _vm.closeCreateDialog
    }
  }, [_vm._v("mdi-close")])], 1)], 1), _vm._v(" "), _c('v-card-text', {
    staticClass: "pt-3"
  }, [_c('ApiForm', {
    ref: "insertForm",
    attrs: {
      "formElements": _vm.insertFormElements,
      "endpoint": _vm.resource,
      "successFn": _vm.createSuccess,
      "errorFn": _vm.updateError,
      "submit": _vm.insertSubmit,
      "cancel": _vm.insertCancel,
      "color": _vm.formFieldsColor,
      "extractErrorsFn": _vm.extractInsertErrors
    },
    on: {
      "formReset": function ($event) {
        _vm.dialogCreate = false;
      }
    }
  })], 1)], 1)], 1) : _vm._e(), _vm._v(" "), _vm.customDialog ? _c('v-dialog', {
    attrs: {
      "max-width": "500px",
      "fullscreen": _vm.fullscreen
    },
    model: {
      value: _vm.dialogCustom,
      callback: function ($$v) {
        _vm.dialogCustom = $$v;
      },
      expression: "dialogCustom"
    }
  }, [_c('v-card', [_c('v-toolbar', {
    attrs: {
      "color": _vm.modalHeaderColor,
      "dark": ""
    }
  }, [_c('v-toolbar-title', [_vm._v("Custom")]), _vm._v(" "), _c('v-spacer'), _vm._v(" "), _c('v-btn', {
    attrs: {
      "icon": ""
    }
  }, [_c('v-icon', {
    on: {
      "click": _vm.closeCustomDialog
    }
  }, [_vm._v("mdi-close")])], 1)], 1), _vm._v(" "), _c('v-card-text', {
    staticClass: "pt-3"
  }, [_c(_vm.customDialog.component, {
    tag: "component",
    attrs: {
      "data": _vm.customDialogData
    },
    on: {
      "close": function ($event) {
        _vm.dialogCustom = false;
      }
    }
  })], 1)], 1)], 1) : _vm._e()], 2);
};

var __vue_staticRenderFns__ = [];
/* style */

const __vue_inject_styles__ = undefined;
/* scoped */

const __vue_scope_id__ = undefined;
/* module identifier */

const __vue_module_identifier__ = undefined;
/* functional template */

const __vue_is_functional_template__ = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__ = /*#__PURE__*/normalizeComponent({
  render: __vue_render__,
  staticRenderFns: __vue_staticRenderFns__
}, __vue_inject_styles__, __vue_script__, __vue_scope_id__, __vue_is_functional_template__, __vue_module_identifier__, false, undefined, undefined, undefined);

var components = /*#__PURE__*/Object.freeze({
  __proto__: null,
  FormBuilder: __vue_component__$5,
  ApiForm: __vue_component__$4,
  SearchForm: __vue_component__$3,
  RestUpdateForm: __vue_component__$2,
  ApiTable: __vue_component__$1,
  RestTable: __vue_component__
});

// Import vue components

const install = function installVuetifyFormBuilder(Vue) {
  if (install.installed) return;
  install.installed = true;
  Object.entries(components).forEach(([componentName, component]) => {
    Vue.component(componentName, component);
  });
}; // Create module definition for Vue.use()


const plugin = {
  install
}; // To auto-install on non-es builds, when vue is found

export default plugin;
export { __vue_component__$4 as ApiForm, __vue_component__$1 as ApiTable, __vue_component__$5 as FormBuilder, __vue_component__ as RestTable, __vue_component__$2 as RestUpdateForm, __vue_component__$3 as SearchForm };
